import { useEffect } from "react";
import { usePageHeader } from "contexts/PageHeaderContext";
import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Badge } from "../../ui/badge";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Flag,
  X,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

interface CurrencyAmount {
  currency: string;
  amount: number;
}

interface Nationality {
  id: string;
  nationalityName: string;
  category: "standard" | "special" | "exempt";
  currencies: CurrencyAmount[];
  isActive: boolean;
}

const availableCurrencies = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "IQD", name: "Iraqi Dinar", symbol: "IQD" },
  { code: "IRR", name: "Iranian Rial", symbol: "IRR" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "PKR", name: "Pakistani Rupee", symbol: "Rs" },
];

export const NationalityMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "Nationality Master",
      childTitle: "Manage nationalities and their visa fee structures",
      icon: <Flag className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);

  const [formData, setFormData] = useState<Partial<Nationality>>({
    nationalityName: "",
    category: "standard",
    currencies: [{ currency: "", amount: 0 }],
    isActive: true,
  });

  const [nationalities, setNationalities] = useState<Nationality[]>([
    {
      id: "1",
      nationalityName: "Indian",
      category: "standard",
      currencies: [
        { currency: "USD", amount: 75 },
        { currency: "INR", amount: 6000 },
      ],
      isActive: true,
    },
    {
      id: "2",
      nationalityName: "Pakistani",
      category: "standard",
      currencies: [
        { currency: "USD", amount: 75 },
        { currency: "PKR", amount: 21000 },
      ],
      isActive: true,
    },
    {
      id: "3",
      nationalityName: "Bangladeshi",
      category: "standard",
      currencies: [{ currency: "USD", amount: 75 }],
      isActive: true,
    },
    {
      id: "4",
      nationalityName: "Iraqi",
      category: "exempt",
      currencies: [],
      isActive: true,
    },
    {
      id: "5",
      nationalityName: "American",
      category: "special",
      currencies: [{ currency: "USD", amount: 150 }],
      isActive: true,
    },
  ]);

  const filteredNationalities = nationalities.filter((nat) =>
    nat.nationalityName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      nationalityName: "",
      category: "standard",
      currencies: [{ currency: "", amount: 0 }],
      isActive: true,
    });
    setShowDialog(true);
  };

  const handleEdit = (nationality: Nationality) => {
    setEditingId(nationality.id);
    setFormData({
      nationalityName: nationality.nationalityName,
      category: nationality.category,
      currencies:
        nationality.currencies.length > 0
          ? nationality.currencies
          : [{ currency: "", amount: 0 }],
      isActive: nationality.isActive,
    });
    setShowDialog(true);
  };

  //   const handleDelete = (id: string) => {
  //     if (confirm('Are you sure you want to delete this nationality?')) {
  //       setNationalities(nationalities.filter(nat => nat.id !== id));
  //       toast.success('Nationality deleted successfully');
  //     }
  //   };

  const handleSave = () => {
    if (!formData.nationalityName) {
      toast.error("Please enter nationality name");
      return;
    }

    if (editingId) {
      setNationalities(
        nationalities.map((nat) =>
          nat.id === editingId ? ({ ...nat, ...formData } as Nationality) : nat
        )
      );
      toast.success("Nationality updated successfully");
    } else {
      const newNationality: Nationality = {
        ...formData,
        id: Date.now().toString(),
      } as Nationality;
      setNationalities([...nationalities, newNationality]);
      toast.success("Nationality added successfully");
    }

    handleCloseDialog();
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingId(null);
    setFormData({
      nationalityName: "",
      category: "standard",
      currencies: [{ currency: "", amount: 0 }],
      isActive: true,
    });
  };

  const addCurrencyField = () => {
    setFormData({
      ...formData,
      currencies: [...(formData.currencies || []), { currency: "", amount: 0 }],
    });
  };

  const removeCurrencyField = (index: number) => {
    setFormData({
      ...formData,
      currencies: (formData.currencies || []).filter((_, i) => i !== index),
    });
  };

  const updateCurrency = (
    index: number,
    field: "currency" | "amount",
    value: string | number
  ) => {
    const updatedCurrencies = [...(formData.currencies || [])];
    updatedCurrencies[index] = {
      ...updatedCurrencies[index],
      [field]: value,
    };
    setFormData({ ...formData, currencies: updatedCurrencies });
  };

  const getCategoryColor = (category: string) => {
    const key = category?.toString().trim().toLowerCase();

    const colors = {
      standard: "!bg-blue-50 !text-blue-700 !border-blue-300",
      special: "!bg-purple-50 !text-purple-700 !border-purple-300",
      exempt: "!bg-green-50 !text-green-700 !border-green-300",
    };

    return (
      colors[key as keyof typeof colors] ||
      "!bg-gray-50 !text-gray-700 !border-gray-300"
    );
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowStatistics(!showStatistics)}
            className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
          >
            {showStatistics ? (
              <ChevronUp className="w-4 h-4 mr-2" />
            ) : (
              <ChevronDown className="w-4 h-4 mr-2" />
            )}
            Statistics
          </Button>
          <Button
            onClick={handleAdd}
            style={{ background: "#5B9BD5" }}
            className="text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Nationality
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {showStatistics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Total Nationalities</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {nationalities.length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Flag className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Active</p>
                  <p className="text-2xl font-bold text-green-900">
                    {nationalities.filter((n) => n.isActive).length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Special Category</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {
                      nationalities.filter((n) => n.category === "special")
                        .length
                    }
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Flag className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Exempt</p>
                  <p className="text-2xl font-bold text-amber-900">
                    {
                      nationalities.filter((n) => n.category === "exempt")
                        .length
                    }
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Flag className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card className="bg-white border-[#5B9BD5]/30">
        <CardContent className="pt-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search nationalities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px" }}>
        <CardHeader className="border-b bg-gradient-to-r from-sky-50 to-blue-50">
          <CardTitle style={{ color: "#5B9BD5" }}>Nationalities</CardTitle>
          <CardDescription className="pb-6">
            {filteredNationalities.length} nationalit
            {filteredNationalities.length !== 1 ? "ies" : "y"} found
          </CardDescription>
        </CardHeader>
        <CardContent style={{ padding: "0px" }}>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Nationality
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Category
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Fee Structure
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs"
                  >
                    Status
                  </TableHead>
                  <TableHead
                    style={{ color: "#5B9BD5" }}
                    className="px-4 py-3 text-xs text-right"
                  >
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNationalities.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center py-8 px-4 text-gray-500 text-sm"
                    >
                      No nationalities found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredNationalities.map((nationality) => (
                    <TableRow
                      key={nationality.id}
                      className="hover:bg-sky-50/30"
                    >
                      <TableCell className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                            <Flag className="w-4 h-4 text-[#5B9BD5]" />
                          </div>
                          <span className="text-gray-900 text-sm">
                            {nationality.nationalityName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge
                          className={`${getCategoryColor(nationality.category)} text-[10px] px-1.5 py-0.5`}
                        >
                          {nationality.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {nationality.currencies.length > 0 ? (
                            nationality.currencies.map((curr, idx) => (
                              <Badge
                                key={idx}
                                variant="outline"
                                className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] px-1.5 py-0.5"
                              >
                                {curr.currency}: {curr.amount}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-xs text-gray-500">
                              No fees
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="px-4 py-3">
                        <Badge
                          className={`border text-[10px] px-1.5 py-0.5 rounded-full 
    ${
      nationality.isActive
        ? "!bg-green-100 !text-green-700 !border-green-300"
        : "!bg-gray-100 !text-gray-700 !border-gray-300"
    }`}
                        >
                          {nationality.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(nationality)}
                            className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                            title="Edit nationality"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            // onClick={() => handleDelete(nationality.id)}
                            className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                            title="Delete nationality"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-gray-200">
            {filteredNationalities.length === 0 ? (
              <div className="text-center py-8 px-4 text-gray-500 text-sm">
                No nationalities found
              </div>
            ) : (
              filteredNationalities.map((nationality) => (
                <div
                  key={nationality.id}
                  className="p-4 hover:bg-sky-50/30 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                        <Flag className="w-5 h-5 text-[#5B9BD5]" />
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {nationality.nationalityName}
                        </h3>
                        <Badge
                          className={`${getCategoryColor(nationality.category)} text-[10px] px-1.5 py-0.5 mt-1`}
                        >
                          {nationality.category}
                        </Badge>
                      </div>
                    </div>
                    <Badge
                      className={`${nationality.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                    >
                      {nationality.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>

                  {nationality.currencies.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-600 mb-1">
                        Fee Structure:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {nationality.currencies.map((curr, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] px-1.5 py-0.5"
                          >
                            {curr.currency}: {curr.amount}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(nationality)}
                      className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                    >
                      <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      //   onClick={() => handleDelete(nationality.id)}
                      className="flex-1 text-red-600 border-red-300 hover:bg-red-50 h-8"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ color: "#5B9BD5" }}>
              {editingId ? "Edit Nationality" : "Add Nationality"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Update nationality details and fee structure"
                : "Add a new nationality with fee structure"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nationalityName" className="text-sm">
                Nationality Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="nationalityName"
                placeholder="e.g., Indian, Pakistani, American"
                value={formData.nationalityName}
                onChange={(e) =>
                  setFormData({ ...formData, nationalityName: e.target.value })
                }
                className="text-sm"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category" className="text-sm">
                Category <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value: Nationality["category"]) =>
                  setFormData({ ...formData, category: value })
                }
              >
                <SelectTrigger id="category" className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="special">Special</SelectItem>
                  <SelectItem value="exempt">Exempt</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {formData.category === "exempt"
                  ? "Exempt nationalities have no visa fees"
                  : "Define currency-wise fees below"}
              </p>
            </div>

            {formData.category !== "exempt" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Fee Structure</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addCurrencyField}
                    className="text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-[#5B9BD5]/10"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Currency
                  </Button>
                </div>

                {(formData.currencies || []).map((curr, index) => (
                  <div
                    key={index}
                    className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1 space-y-2">
                      <Select
                        value={curr.currency}
                        onValueChange={(value) =>
                          updateCurrency(index, "currency", value)
                        }
                      >
                        <SelectTrigger className="text-sm h-9">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableCurrencies.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.code} - {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1 space-y-2">
                      <Input
                        type="number"
                        placeholder="Amount"
                        value={curr.amount}
                        onChange={(e) =>
                          updateCurrency(
                            index,
                            "amount",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="text-sm h-9"
                      />
                    </div>
                    {(formData.currencies || []).length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCurrencyField(index)}
                        className="text-red-600 hover:bg-red-50 h-9 w-9 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseDialog}
              className="text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              style={{ background: "#5B9BD5" }}
              className="text-white text-sm"
            >
              {editingId ? "Update" : "Add"} Nationality
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
